import React, { useState, useEffect } from 'react';
import axios from 'axios';

const HypothesesForm = ({ journeyId, token, onSuccess, onCancel }) => {
  const [hypotheses, setHypotheses] = useState(['']);
  const [supporting_evidence, setSupporting_evidence] = useState('');
  const [assumptions, setAssumptions] = useState('');
  const [risks, setRisks] = useState('');
  const [success_criteria, setSuccess_criteria] = useState('');

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [existingHypotheses, setExistingHypotheses] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);

  // Scoring state
  const [hypothesesData, setHypothesesData] = useState(null);
  const [scoringMode, setScoringMode] = useState(false);
  const [scores, setScores] = useState({
    confidence_score: 5,
    impact_score: 5,
    testability_score: 5,
    priority_score: 5,
    comment: '',
  });

  // Load hypotheses on mount
  useEffect(() => {
    loadHypotheses();
  }, [journeyId, token]);

  const loadHypotheses = async () => {
    try {
      const response = await axios.get(
        `http://localhost:5000/api/journeys/${journeyId}/hypotheses`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.hypotheses) {
        setHypothesesData(response.data.hypotheses);
        setExistingHypotheses(response.data.hypotheses);
        // Populate form fields if not yet submitted
        if (!isEditing && response.data.hypotheses.hypotheses) {
          setHypotheses(response.data.hypotheses.hypotheses);
          setSupporting_evidence(response.data.hypotheses.supporting_evidence || '');
          setAssumptions(response.data.hypotheses.assumptions || '');
          setRisks(response.data.hypotheses.risks || '');
          setSuccess_criteria(response.data.hypotheses.success_criteria || '');
        }
      }
    } catch (err) {
      console.error('Load hypotheses error:', err);
    }
  };

  const handleHypothesisChange = (index, value) => {
    const newHypotheses = [...hypotheses];
    newHypotheses[index] = value;
    setHypotheses(newHypotheses);
  };

  const handleAddHypothesis = () => {
    setHypotheses([...hypotheses, '']);
  };

  const handleRemoveHypothesis = (index) => {
    if (hypotheses.length > 1) {
      const newHypotheses = hypotheses.filter((_, i) => i !== index);
      setHypotheses(newHypotheses);
    }
  };

  const isFormValid = () => {
    const validHypotheses = hypotheses.filter(h => h.trim().length >= 10).length;
    return (
      validHypotheses >= 1 &&
      supporting_evidence.trim().length >= 10 &&
      success_criteria.trim().length >= 10
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const validHypotheses = hypotheses
        .filter(h => h.trim().length > 0)
        .map(h => h.trim());

      const payload = {
        hypotheses: validHypotheses,
        supporting_evidence: supporting_evidence.trim(),
        assumptions: assumptions.trim() || null,
        risks: risks.trim() || null,
        success_criteria: success_criteria.trim(),
      };

      const response = await axios.post(
        `http://localhost:5000/api/journeys/${journeyId}/hypotheses`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccessMessage('Hypotheses submitted successfully! Ready for experimentation.');
      setHypothesesData(response.data.hypotheses);
      setExistingHypotheses(response.data.hypotheses);
      setIsEditing(false);
      // Reset form
      setHypotheses(['']);
      setSupporting_evidence('');
      setAssumptions('');
      setRisks('');
      setSuccess_criteria('');
      setScoringMode(true);

      if (onSuccess) {
        setTimeout(() => onSuccess(response.data.hypotheses), 1500);
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to submit hypotheses. Please try again.';
      setErrorMessage(errorMsg);
      console.error('Hypotheses submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreHypotheses = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(
        `http://localhost:5000/api/journeys/${journeyId}/hypotheses/${hypothesesData.id}/score`,
        scores,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccessMessage(`Hypotheses rated successfully! Overall Score: ${response.data.scores.overall_score}/10`);
      setScores({ confidence_score: 5, impact_score: 5, testability_score: 5, priority_score: 5, comment: '' });
      await loadHypotheses();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to score hypotheses';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (e) => {
    const { name, value } = e.target;
    if (name === 'comment') {
      setScores({ ...scores, [name]: value });
    } else {
      setScores({ ...scores, [name]: parseInt(value) });
    }
  };

  // Scoring view
  if (hypothesesData && scoringMode) {
    const avgScores = hypothesesData.scores?.average_scores;

    return (
      <div className="form-card">
        <h2 className="form-title">🔬 Rate Hypotheses</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Score the hypotheses across 4 dimensions
        </p>

        {successMessage && <div className="form-success">{successMessage}</div>}
        {errorMessage && <div className="form-error">{errorMessage}</div>}

        {/* Display Hypotheses */}
        <div style={{ background: '#f9faf9', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', color: '#2c3e50' }}>📋 Hypotheses</h3>
          {hypothesesData.hypotheses.map((h, idx) => (
            <div key={idx} style={{ marginBottom: '10px', paddingLeft: '20px' }}>
              <p style={{ margin: '0', color: '#333', fontSize: '14px' }}>
                <strong>{idx + 1}.</strong> {h}
              </p>
            </div>
          ))}
        </div>

        {/* Current Scores */}
        {avgScores && avgScores.confidence && (
          <div style={{ background: '#f0f6ff', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #d0e8ff' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', color: '#0066cc' }}>📊 Current Ratings (Averages)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>Confidence</p>
                <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#0066cc' }}>{avgScores.confidence}/10</p>
              </div>
              <div>
                <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>Impact</p>
                <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#0066cc' }}>{avgScores.impact}/10</p>
              </div>
              <div>
                <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>Testability</p>
                <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#0066cc' }}>{avgScores.testability}/10</p>
              </div>
              <div>
                <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>Priority</p>
                <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#0066cc' }}>{avgScores.priority}/10</p>
              </div>
            </div>
            <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#7f8c8d' }}>
              Based on {hypothesesData.scores.total_ratings} rating(s)
            </p>
          </div>
        )}

        {/* Scoring Form */}
        <form onSubmit={handleScoreHypotheses}>
          <div style={{ background: '#f9faf9', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #e0e0e0' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '15px', color: '#2c3e50' }}>⭐ Add Your Rating</h3>

            {/* Confidence Score */}
            <div className="form-group">
              <label>How confident are we this will work? (1-10)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <input
                  type="range"
                  name="confidence_score"
                  min="1"
                  max="10"
                  value={scores.confidence_score}
                  onChange={handleScoreChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 'bold', minWidth: '30px', textAlign: 'right' }}>
                  {scores.confidence_score}
                </span>
              </div>
              <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                1 = Not at all | 10 = Very confident
              </p>
            </div>

            {/* Impact Score */}
            <div className="form-group">
              <label>What's the potential impact? (1-10)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <input
                  type="range"
                  name="impact_score"
                  min="1"
                  max="10"
                  value={scores.impact_score}
                  onChange={handleScoreChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 'bold', minWidth: '30px', textAlign: 'right' }}>
                  {scores.impact_score}
                </span>
              </div>
              <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                1 = Low impact | 10 = High impact
              </p>
            </div>

            {/* Testability Score */}
            <div className="form-group">
              <label>How easy is this to test? (1-10)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <input
                  type="range"
                  name="testability_score"
                  min="1"
                  max="10"
                  value={scores.testability_score}
                  onChange={handleScoreChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 'bold', minWidth: '30px', textAlign: 'right' }}>
                  {scores.testability_score}
                </span>
              </div>
              <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                1 = Very difficult | 10 = Easy to test
              </p>
            </div>

            {/* Priority Score */}
            <div className="form-group">
              <label>How important is this to test first? (1-10)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <input
                  type="range"
                  name="priority_score"
                  min="1"
                  max="10"
                  value={scores.priority_score}
                  onChange={handleScoreChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 'bold', minWidth: '30px', textAlign: 'right' }}>
                  {scores.priority_score}
                </span>
              </div>
              <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
                1 = Low priority | 10 = Critical
              </p>
            </div>

            {/* Comment */}
            <div className="form-group">
              <label>Comment (optional)</label>
              <textarea
                name="comment"
                value={scores.comment}
                onChange={handleScoreChange}
                placeholder="Add notes about your rating..."
                maxLength="500"
              />
              <div className="char-count">{scores.comment.length}/500</div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setScoringMode(false)}
              disabled={loading}
            >
              Back
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Rating'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Read-only view for completed hypotheses
  if (existingHypotheses && !isEditing) {
    return (
      <div className="form-card" style={{ backgroundColor: '#f9f9f9', opacity: 0.95 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 className="form-title">🔬 Generate Hypotheses</h2>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#d4edda',
              color: '#155724',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '600',
              marginTop: '8px',
            }}>
              ✓ COMPLETE
            </div>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => setShowConfirmEdit(true)}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            ✏️ Edit
          </button>
        </div>

        {successMessage && <div className="form-success">{successMessage}</div>}
        {errorMessage && <div className="form-error">{errorMessage}</div>}

        {showConfirmEdit && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }} onClick={() => setShowConfirmEdit(false)}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '400px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}>Edit Completed Hypotheses?</h3>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                These hypotheses have already been marked complete. Changes will be logged in the audit trail.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setShowConfirmEdit(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setIsEditing(true);
                    setShowConfirmEdit(false);
                  }}
                >
                  Continue Editing
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          {hypotheses && hypotheses.length > 0 && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Hypotheses</h4>
              <ul style={{ color: '#555', margin: '0', paddingLeft: '20px' }}>
                {hypotheses.map((h, idx) => (
                  <li key={idx} style={{ marginBottom: '8px' }}>{h}</li>
                ))}
              </ul>
            </div>
          )}
          {supporting_evidence && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Supporting Evidence</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{supporting_evidence}</p>
            </div>
          )}
          {assumptions && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Key Assumptions</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{assumptions}</p>
            </div>
          )}
          {risks && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Potential Risks</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{risks}</p>
            </div>
          )}
          {success_criteria && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Success Criteria</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{success_criteria}</p>
            </div>
          )}
        </div>

        <div className="form-actions">
          {onCancel && <button className="btn btn-secondary" onClick={onCancel}>Back</button>}
        </div>
      </div>
    );
  }

  // Form submission view
  return (
    <div className="form-card">
      <h2 className="form-title">🔬 Generate Hypotheses</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>Step 3 of 5: Develop testable hypotheses</p>

      {successMessage && <div className="form-success">{successMessage}</div>}
      {errorMessage && <div className="form-error">{errorMessage}</div>}

      <form onSubmit={handleSubmit}>
        {/* Hypotheses Section */}
        <div className="form-group">
          <label>Hypotheses * (at least 1 required)</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '15px' }}>
            Enter potential solutions or changes that might address the problem (min 10 chars each)
          </p>

          <div className="hypotheses-list">
            {hypotheses.map((hypothesis, index) => (
              <div key={index} className="hypothesis-item">
                <div className="hypothesis-header">
                  <span className="hypothesis-number">Hypothesis {index + 1}</span>
                  {hypotheses.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove-hypothesis"
                      onClick={() => handleRemoveHypothesis(index)}
                      title="Remove this hypothesis"
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>
                <textarea
                  value={hypothesis}
                  onChange={(e) => handleHypothesisChange(index, e.target.value)}
                  placeholder="e.g., 'If we reduce checkout steps, conversion will increase by 25%'"
                  maxLength="500"
                />
                <div className="char-count">
                  {hypothesis.length}/500 characters
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleAddHypothesis}
            style={{ marginTop: '15px', marginBottom: '20px' }}
          >
            + Add Another Hypothesis
          </button>
        </div>

        {/* Supporting Evidence */}
        <div className="form-group">
          <label>Supporting Evidence *</label>
          <textarea
            value={supporting_evidence}
            onChange={(e) => setSupporting_evidence(e.target.value)}
            placeholder="What data or observations support these hypotheses? (min 10 chars)"
            maxLength="1000"
          />
          <div className="char-count">
            {supporting_evidence.length}/1000 characters
          </div>
        </div>

        {/* Assumptions */}
        <div className="form-group">
          <label>Key Assumptions</label>
          <textarea
            value={assumptions}
            onChange={(e) => setAssumptions(e.target.value)}
            placeholder="What are we assuming to be true? What might we be wrong about?"
            maxLength="1000"
          />
          <div className="char-count">
            {assumptions.length}/1000 characters
          </div>
        </div>

        {/* Risks */}
        <div className="form-group">
          <label>Potential Risks</label>
          <textarea
            value={risks}
            onChange={(e) => setRisks(e.target.value)}
            placeholder="What could go wrong if we pursue these hypotheses?"
            maxLength="1000"
          />
          <div className="char-count">
            {risks.length}/1000 characters
          </div>
        </div>

        {/* Success Criteria */}
        <div className="form-group">
          <label>Success Criteria *</label>
          <textarea
            value={success_criteria}
            onChange={(e) => setSuccess_criteria(e.target.value)}
            placeholder="How will we know if a hypothesis is validated? What metrics matter? (min 10 chars)"
            maxLength="1000"
          />
          <div className="char-count">
            {success_criteria.length}/1000 characters
          </div>
        </div>

        {/* Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setHypotheses(['']);
              setSupporting_evidence('');
              setAssumptions('');
              setRisks('');
              setSuccess_criteria('');
            }}
            disabled={loading}
          >
            Clear
          </button>
          {onCancel && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Back
            </button>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !isFormValid()}
          >
            {loading ? <span className="loading">⏳</span> : 'Submit Hypotheses'}
          </button>
        </div>
      </form>

      <style>{`
        .hypotheses-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 24px;
        }

        .hypothesis-item {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 18px;
          background: #f8faf9;
          border-radius: 8px;
          border-left: 4px solid #8FC5B2;
        }

        .hypothesis-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .hypothesis-number {
          font-size: 12px;
          font-weight: 600;
          color: #8FC5B2;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .hypothesis-item textarea {
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 12px 14px;
          font-family: inherit;
          font-size: 14px;
          line-height: 1.5;
          resize: vertical;
          min-height: 80px;
          width: 100%;
          box-sizing: border-box;
        }

        .hypothesis-item textarea:focus {
          outline: none;
          border-color: #8FC5B2;
          box-shadow: 0 0 0 3px rgba(143, 197, 178, 0.15);
        }

        .btn-remove-hypothesis {
          background: #ff6b6b;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 6px 12px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
          transition: all 0.2s ease;
        }

        .btn-remove-hypothesis:hover {
          background: #ff5252;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(255, 82, 82, 0.2);
        }

        .btn-remove-hypothesis:active {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
};

export default HypothesesForm;
